import React, { useState, useEffect } from 'react';

const MetaBudgetReviewHistory = ({ recordId, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHistory();
  }, [recordId]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      
      // Determinar la URL de la API basada en el entorno
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_API_URL || 'https://dashboard.doctorweb.top/api'
        : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

      const response = await fetch(`${API_URL}/meta-budget-review/${recordId}/history`);
      
      if (response.ok) {
        const data = await response.json();
        setHistory(data);
      } else {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error al cargar historial:', error);
      alert('Error al cargar el historial del registro');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionIcon = (actionType) => {
    const icons = {
      'created': '➕',
      'updated': '✏️',
      'status_changed': '🔄',
      'comment_added': '💬',
      'completed': '✅',
      'cancelled': '❌'
    };
    return icons[actionType] || '📝';
  };

  const getActionText = (actionType) => {
    const texts = {
      'created': 'Registro creado',
      'updated': 'Registro actualizado',
      'status_changed': 'Estado cambiado',
      'comment_added': 'Comentario agregado',
      'completed': 'Registro completado',
      'cancelled': 'Registro cancelado'
    };
    return texts[actionType] || actionType;
  };

  const getStatusText = (status) => {
    const texts = {
      'pendiente': 'Pendiente',
      'en_proceso': 'En Proceso',
      'completado': 'Completado',
      'cancelado': 'Cancelado'
    };
    return texts[status] || status;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6">
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Cargando historial...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">📋 Historial de Revisión de Presupuesto</h2>
              <p className="text-blue-100 mt-1">
                Registro completo de cambios y acciones realizadas
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-blue-200 text-2xl font-bold"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {history.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-400 text-4xl mb-4">📋</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Sin historial</h3>
              <p className="text-gray-500">No hay registros de cambios para este elemento.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {history.map((entry, index) => (
                <div
                  key={entry.id}
                  className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-500"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <div className="text-2xl">
                        {getActionIcon(entry.action_type)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h4 className="font-semibold text-gray-900">
                            {getActionText(entry.action_type)}
                          </h4>
                          {entry.action_type === 'status_changed' && (
                            <div className="flex items-center space-x-2 text-sm">
                              {entry.old_status && (
                                <>
                                  <span className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs">
                                    {getStatusText(entry.old_status)}
                                  </span>
                                  <span className="text-gray-400">→</span>
                                </>
                              )}
                              {entry.new_status && (
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                                  {getStatusText(entry.new_status)}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {entry.comment && (
                          <div className="mt-2 p-3 bg-white rounded border">
                            <p className="text-gray-700 text-sm whitespace-pre-wrap">
                              {entry.comment}
                            </p>
                          </div>
                        )}
                        
                        <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                          <span>👤 {entry.user_name || 'Sistema'}</span>
                          <span>🕒 {formatDate(entry.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetaBudgetReviewHistory;
